import api from '../services';
import type {
  BuildHeadersOptions,
  ExecuteRequestResult,
  HandlerEvaluationResult,
  NormalizedFieldMapping,
  ParsedLeadOutcome,
  FieldMapping,
  AuthError,
  VariableValues,
} from '../types/testVendorRequest.types';

// ============ Helper Functions ============
const FORBIDDEN_HEADERS: Set<string> = new Set([
  'host',
  'origin',
  'referer',
  'content-length',
  'connection',
]);

export const COMMON_KEYS: string[] = [
  'email', 'firstName', 'lastName', 'phone', 'ip', 'address1', 'city', 'state', 'zip', 'userAgent',
  'signup_date', 'session_id', 'source_id', 'source_sub_id'
];

const VAR_REGEX = /\{\{\s*([a-zA-Z0-9_\.\-]+)\s*\}\}|\[\{\s*([a-zA-Z0-9_\.\-]+)\s*\}\]/g;

const ALIASES: Record<string, string> = {
  first_name: 'firstName', last_name: 'lastName', ip_addr: 'ip',
  signup_date: 'signupDate', session_id: 'sessionId', source_id: 'sourceId', source_sub_id: 'sourceSubId',
};

const toCamel = (str: string): string => str.replace(/_([a-z])/g, (_, result: string) => result.toUpperCase());
const toSnake = (str: string): string => str.replace(/[A-Z]/g, (result: string) => '_' + result.toLowerCase());

/**
 * Build a header object from an array of header lines.
 * @param headerLines - Array of header lines.
 * @param basicAuthUsername - Basic auth username.
 * @param basicAuthPassword - Basic auth password.
 * @param options - Options object.
 * @returns Headers object.
 */
export const buildHeadersObject = (
  headerLines: string[] = [],
): Record<string, string> => {
  const headersObj: Record<string, string> = {};

  headerLines.forEach((line) => {

    const idx = line.indexOf(':');
    if (idx === -1) return;

    const normalizedKey = line.slice(0, idx).toLowerCase().trim();
    const val = line.slice(idx + 1).trim();

    if (!normalizedKey || FORBIDDEN_HEADERS.has(normalizedKey)) return;

    headersObj[normalizedKey] = val;
  });

  return headersObj;
};

/**
 * Execute a request to the vendor endpoint.
 */
export const executeRequest = async (
  finalUrl: string,
  method: string = 'GET',
  headersObj: Record<string, string> = {},
  timeoutMs: number = 30000,
  finalBody?: string,
): Promise<ExecuteRequestResult | undefined> => {
  if (!finalUrl) return;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const httpMethod = method === 'GET' ? 'GET' : 'POST';

    const payload = {
      url: finalUrl.trim(),
      method: httpMethod,
      headers: headersObj,
      body: httpMethod === 'POST' ? finalBody : undefined,
    };

    const res = await api.post('api/vendor/send', payload, {signal: controller.signal});

    let rawResponse = typeof res.data === 'string' ? res.data : JSON.stringify(res.data);
    let status = res.status;

    let envelope: string | object = rawResponse;
    let handlerBody = rawResponse;

    const parsed = typeof res.data === 'object' ? res.data : null;

    if (parsed) {
      if (typeof (parsed as any).statusCode === 'number') status = (parsed as any).statusCode;

      if ((parsed as any).body != null) {
        handlerBody = typeof (parsed as any).body === 'string' ? (parsed as any).body : JSON.stringify((parsed as any).body);
      } else if ((parsed as any).text != null) {
        handlerBody = String((parsed as any).text);
      }

      envelope = parsed;
    }

    return {status, text: handlerBody, envelope};
  } catch (err) {
    const error = err as any;

    // Prefer backend response body if present
    const status = error?.response?.status ?? error?.status;
    const data = error?.response?.data;
    const envelope = typeof data === 'string' ? data : data != null ? JSON.stringify(data) : '';
    const text = envelope;

    console.warn('Failed to execute request', err);
    throw err;
  } finally {
    clearTimeout(timer);
  }
};

export const resolveValue = (values: VariableValues | null | undefined, key: string): string => {
  if (!values || !key) return '';
  const direct = values[key];
  if (direct != null) return String(direct);

  const aliasKey = ALIASES[key];
  if (aliasKey && values[aliasKey] != null) return String(values[aliasKey]);

  const camelKey = toCamel(key);
  if (values[camelKey] != null) return String(values[camelKey]);

  const snakeKey = toSnake(key);
  if (values[snakeKey] != null) return String(values[snakeKey]);

  return '';
};

export const extractVariables = (text: string | null | undefined): string[] => {
  if (!text || typeof text !== 'string') return [];
  const matches = text.match(VAR_REGEX) || [];
  return [...new Set(matches.map(match => {
    const innerMatch = match.match(/[a-zA-Z0-9_\.\-]+/);
    return innerMatch ? innerMatch[0] : '';
  }).filter(Boolean))];
};

/**
 * Render a template with the given values.
 */
export const renderTemplate = (template: string | null | undefined, values: VariableValues | null | undefined): string => {
  if (template == null) return '';
  return String(template).replace(VAR_REGEX, (match, group1: string, group2: string) => {
    const key = group1 || group2;
    const val = resolveValue(values, key);
    return val == null ? '' : String(val);
  });
};

/**
 * Parse lead outcome details from a JSON response without altering existing handler semantics.
 * Extracts optional fields: leadId, result, reason. Case-insensitive key matching.
 */
export const parseLeadOutcome = (rawText: string | object | null | undefined): ParsedLeadOutcome => {
  try {
    const textToParse = typeof rawText === 'object' ? JSON.stringify(rawText) : rawText;
    const parsed = JSON.parse(textToParse || '');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const mapLowerToOrig = new Map<string, string>(Object.keys(parsed).map((k) => [k.toLowerCase(), k]));
    const getVal = (candidates: string[]): unknown => {
      for (const name of candidates) {
        const orig = mapLowerToOrig.get(String(name).toLowerCase());
        if (orig && parsed[orig] !== undefined) return parsed[orig];
      }
      return undefined;
    };

    let leadId = getVal(['leadid', 'lead_id', 'leadId']) as string | number | undefined;
    if (typeof leadId === 'string' && /^\d+$/.test(leadId)) leadId = Number(leadId);

    let result = getVal(['status', 'result']) as string | undefined;
    if (result != null) result = String(result);

    let reason = getVal(['reason', 'message']) as string | undefined;
    if (reason == null) {
      const errors = getVal(['errors', 'error']);
      if (Array.isArray(errors) && errors.length) {
        reason = String(errors[0]);
      } else if (typeof errors === 'string') {
        reason = errors;
      } else if (errors && typeof errors === 'object') {
        // Handle Java Map<String, String> serialized as a JSON object
        // Prefer "key: value" pairs, but fall back to values only if keys are empty.
        const entries = Object.entries(errors as Record<string, unknown>).filter(([, value]) => value != null && String(value) !== '');
        if (entries.length) {
          const parts = entries.map(([key, value]) => (key ? `${key}: ${value}` : String(value)));
          reason = parts.join('; ');
        }
      }
    }

    const outcome: ParsedLeadOutcome = {};
    if (leadId != null) outcome.leadId = leadId;
    if (result != null) outcome.result = result;
    if (reason != null) outcome.reason = String(reason);
    return outcome;
  } catch {
    return {};
  }
};

/**
 * Returns unique query parameter names whose values are non-empty and
 * do not contain unresolved template markers (e.g. {{ }} or [{ }]).
 */
export const getResolvedQueryParamNames = (urlString: string | null | undefined): string[] => {
  if (!urlString) return [];
  const TEMPLATE_PATTERN = /\{\{|\[\{/;
  const names = new Set<string>();
  const query = urlString.includes('?') ? urlString.split('?')[1] : urlString;

  if (!query) return [];

  for (const pair of query.split('&')) {
    if (!pair) continue;
    const [rawKey, ...rest] = pair.split('=');
    const rawValue = rest.join('=') || '';
    const value = decodeURIComponent(rawValue.replace(/\+/g, ' '));
    if (!rawKey) continue;
    if (value === '' || TEMPLATE_PATTERN.test(value)) continue;
    names.add(rawKey.trim());
  }

  return Array.from(names);
};

/**
 * Get request derived values from a URL and body string.
 */
export const getRequestDerivedValues = (urlStr: string | null | undefined, bodyStr: string | null | undefined): Record<string, string | null> => {
  const outcome: Record<string, string | null> = {};

  // URL query params
  try {
    if (urlStr) {
      const url = new URL(urlStr, urlStr?.startsWith('http') ? undefined : 'http://example.com');
      for (const [key, val] of url.searchParams.entries()) outcome[key] = val;
    }
  } catch {
    const index = (urlStr || '').indexOf('?');
    if (index > -1) {
      (urlStr || '')
        .slice(index + 1)
        .split('&')
        .forEach((pair) => {
          const [key, val] = pair.split('=');
          if (key) outcome[key] = decodeURIComponent(val || '');
        });
    }
  }

  // Body-derived values
  const trimmed = (bodyStr || '').trim();
  // JSON body top-level primitives
  if (trimmed.startsWith('{')) {
    try {
      const obj = JSON.parse(trimmed) as Record<string, unknown>;
      if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
        Object.entries(obj).forEach(([k, val]) => {
          if (val === null || ['string', 'number', 'boolean'].includes(typeof val)) {
            outcome[k] = val === null ? null : String(val);
          }
        });
      }
    } catch {
      // ignore JSON parse errors and fall through
    }
  } else if (trimmed) {
    // application/x-www-form-urlencoded (querystring-style) body
    const parts = trimmed.split('&');
    parts.forEach((pair) => {
      if (!pair) return;
      const [rawKey, ...rest] = pair.split('=');
      if (!rawKey) return;
      const rawVal = rest.join('=');
      const key = decodeURIComponent(rawKey);
      const value = decodeURIComponent(String(rawVal || '').replace(/\+/g, ' '));
      outcome[key] = value;
    });
  }

  return outcome;
};

interface NormalizedHandlerResult {
  ok: boolean;
  reason: string;
}

/**
 * Normalize a handler result.
 */
const normalizeHandlerResult = (result: boolean | { ok?: boolean; success?: boolean; reason?: string } | unknown): NormalizedHandlerResult => {
  if (typeof result === 'boolean') {
    return { ok: result, reason: result ? 'Handler returned true' : 'Handler returned false' };
  }

  if (result && typeof result === 'object') {
    const obj = result as { ok?: boolean; success?: boolean; reason?: string };
    const ok =
      typeof obj.ok === 'boolean'
        ? obj.ok
        : typeof obj.success === 'boolean'
          ? obj.success
          : Boolean(result);

    const reason = typeof obj.reason === 'string' ? obj.reason : ok ? 'Handler returned ok' : 'Handler returned not ok';
    return { ok, reason };
  }

  const ok = Boolean(result);
  return { ok, reason: ok ? 'Handler returned truthy' : 'Handler returned falsy' };
};

interface BodyContext {
  bodyStr: string;
  body: string | object;
  isJson: boolean;
}

/**
 * Get the body context from a string.
 */
const getBodyContext = (bodyText: string | null | undefined): BodyContext => {
  const bodyStr = bodyText == null ? '' : String(bodyText);
  const bodyTrimmed = bodyStr.trim();

  if (!bodyTrimmed || (!bodyTrimmed.startsWith('{') && !bodyTrimmed.startsWith('['))) {
    return { bodyStr, body: bodyStr, isJson: false };
  }

  try {
    const parsedBody = JSON.parse(bodyStr) as object;
    return { bodyStr, body: parsedBody, isJson: true };
  } catch {
    return { bodyStr, body: bodyStr, isJson: false };
  }
};

/**
 * Evaluate a handler function.
 */
export const evaluateHandler = (
  status: number,
  bodyText: string | null | undefined,
  extraOkStatuses: number[] | undefined,
  code: string | null | undefined
): HandlerEvaluationResult => {
  const statusOk =
    (status >= 200 && status < 300) ||
    (Array.isArray(extraOkStatuses) && extraOkStatuses.includes(status));

  if (!statusOk) return { ok: false, reason: `HTTP status ${status} not in success set` };

  const ctx = getBodyContext(bodyText);

  const handlerSource =
    (code && code.trim()) ||
    `
      if (typeof body === 'string') return /\\bsuccess\\b/i.test(body);
      return true;
    `;

  try {
    const fn = new Function(`
      return function(ctx){
        var status = ctx.status;
        var isJson = ctx.isJson;
        var bodyStr = ctx.bodyStr;
        var body = ctx.body;
        ${handlerSource}
      }
    `)() as (ctx: BodyContext & { status: number }) => unknown;

    const handlerResult = fn({ status, ...ctx });
    return normalizeHandlerResult(handlerResult);
  } catch (e) {
    const error = e as Error;
    return { ok: false, reason: `Handler execution error${error && error.message ? `: ${error.message}` : ''}` };
  }
};

/**
 * Validate that a ZIP code value is at most 5 numeric characters (US basic ZIP, no +4).
 */
export const isZipUpTo5Digits = (value: string | number | null | undefined): boolean => {
  const s = String(value ?? '').trim();
  return /^\d{1,5}$/.test(s);
};

/**
 * Build a field mapping object from a prefix string.
 */
const buildMappingFromPrefix = (prefix: string): NormalizedFieldMapping => {
  return {
    urlKey: prefix,
    bodyKey: `${prefix}Body`,
    methodKey: `${prefix}Method`,
    headerKeys: [`${prefix}Header1`, `${prefix}Header2`, `${prefix}Header3`],
    handlerIdKey: `${prefix}HandlerId`,
  };
};

/**
 * Validate that a value is a valid IPv4 address.
 */
export const isValidIPv4 = (value: string | null | undefined): boolean => {
  const string = String(value ?? '').trim();
  if (!string) return false;

  const ipv4Regex = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
  const match = string.match(ipv4Regex);

  if (!match) return false;

  // Check each octet is between 0-255
  for (let index = 1; index <= 4; index++) {
    const octet = parseInt(match[index] ?? '', 10);
    if (octet < 0 || octet > 255) return false;
  }

  return true;
};

/**
 * Normalize a field mapping object.
 */
export const normalizeFieldMapping = (
  fieldMapping: FieldMapping | null | undefined,
  prefix: string | null | undefined
): NormalizedFieldMapping => {
  if (prefix) return buildMappingFromPrefix(prefix);

  // Backwards-compatible defaults (your "Offer Action POST details screen" style)
  return {
    urlKey: fieldMapping?.url ?? 'url',
    bodyKey: fieldMapping?.body ?? 'body',
    methodKey: fieldMapping?.method ?? 'postMethod',
    headerKeys: [
      fieldMapping?.header1 ?? 'httpHeader1',
      fieldMapping?.header2 ?? 'httpHeader2',
      fieldMapping?.header3 ?? 'httpHeader3',
    ],
    handlerIdKey: fieldMapping?.handlerId ?? 'httpSuccessHandler',
  };
};
